import type { ModelRequest } from '../model';
import type { AgentInputItem } from '../types/aliases';

/**
 * Projects "registration-only" upload references in user messages into a plain
 * text reminder, right before a request is handed to a provider.
 *
 * Why this exists: a user can attach files/images that live in their library
 * and are mounted read-only into the sandbox at `/workspace/documents/`. The UI
 * carries these as `input_image` / `input_file` content parts that hold *no*
 * inline bytes — only `providerData` metadata (path, display name, …). Such
 * reference-only parts cannot go to a provider as-is: the OpenAI chat-completions
 * converter throws on an imageless `input_image`, and a text-only model would
 * break on real image blocks anyway. So at the single model-call choke point we
 * replace them with one `<user-uploaded-files>` text block listing the sandbox
 * paths. The model only ever sees text; the stored items keep the rich parts so
 * the UI can still render thumbnails / file cards.
 *
 * Parts that carry actual data (`image` / `file` set) are left untouched — those
 * are genuine inline inputs and remain the provider converters' concern.
 */
export function flattenUserAttachmentsForModel(
  request: ModelRequest,
): ModelRequest {
  if (!Array.isArray(request.input)) return request;

  let changed = false;
  const viewImageEnabled = isViewImageToolEnabled(request.tools);
  const input = request.input.map((item) => {
    const next = flattenUserMessage(item, { viewImageEnabled });
    if (next !== item) changed = true;
    return next;
  });

  return changed ? { ...request, input } : request;
}

const UPLOAD_BLOCK_OPEN = '<user-uploaded-files>';
const UPLOAD_BLOCK_CLOSE = '</user-uploaded-files>';

type AnyRecord = Record<string, unknown>;

function isRecord(value: unknown): value is AnyRecord {
  return typeof value === 'object' && value !== null;
}

function flattenUserMessage(
  item: AgentInputItem,
  options: { viewImageEnabled: boolean },
): AgentInputItem {
  const record = item as unknown as AnyRecord;
  if (record.role !== 'user') return item;
  if (record.type !== undefined && record.type !== 'message') return item;
  if (!Array.isArray(record.content)) return item;

  const attachmentParts = record.content.filter(isRegistrationAttachmentPart);
  if (attachmentParts.length === 0) return item;

  const keptParts = record.content.filter(
    (part) => !isRegistrationAttachmentPart(part),
  );
  const reminder = buildReminderText(attachmentParts, options);

  return {
    ...record,
    content: [...keptParts, { type: 'input_text', text: reminder }],
  } as unknown as AgentInputItem;
}

/**
 * True for an `input_image` / `input_file` part that references an asset without
 * carrying its bytes — i.e. an upload registration, not an inline input.
 */
function isRegistrationAttachmentPart(part: unknown): boolean {
  if (!isRecord(part)) return false;
  if (part.type === 'input_image') return part.image === undefined;
  if (part.type === 'input_file') return part.file === undefined;
  return false;
}

function buildReminderText(
  parts: unknown[],
  options: { viewImageEnabled: boolean },
): string {
  const lines = parts.map((part) => attachmentLine(part as AnyRecord));
  const hasImage = parts.some(
    (part) => isRecord(part) && part.type === 'input_image',
  );
  return [
    UPLOAD_BLOCK_OPEN,
    '用户在本条消息中上传了以下文件，位于只读目录 /workspace/documents/：',
    ...lines,
    '文本类文件可用文件工具读取其内容。',
    hasImage && options.viewImageEnabled
      ? '图片可用 view_image 按需查看。'
      : null,
    UPLOAD_BLOCK_CLOSE,
  ]
    .filter((line): line is string => typeof line === 'string')
    .join('\n');
}

function attachmentLine(part: AnyRecord): string {
  const data = isRecord(part.providerData) ? part.providerData : {};
  const label = part.type === 'input_image' ? '图片' : '文件';
  return `- ${resolvePath(data)} （${label}）`;
}

function resolvePath(data: AnyRecord): string {
  if (typeof data.path === 'string' && data.path.length > 0) return data.path;
  if (typeof data.storedName === 'string' && data.storedName.length > 0) {
    return `/workspace/documents/${data.storedName}`;
  }
  if (typeof data.displayName === 'string' && data.displayName.length > 0) {
    return data.displayName;
  }
  return '(未知文件)';
}

function isViewImageToolEnabled(tools: ModelRequest['tools']): boolean {
  return tools.some(
    (tool) => tool.type === 'function' && tool.name === 'view_image',
  );
}
